import { useState } from "react";
import { CircleHelp } from "lucide-react";
import Modal from "shared/Modal";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import { resultStatus } from "./conversationStatus";
import { RESULT_GUIDE, TURN_GUIDE } from "./statusGuide";

/** A "?" button that opens the guide to every status: who owes whom a reply (automatic) and each result a person can set, when to use it
 *  and what it changes for this conversation and for later RFPs. */
export default function StatusHelp({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="What do the statuses mean?" title="What do the statuses mean?"
        className={`inline-flex items-center text-muted-foreground hover:text-primary ${className ?? ""}`}>
        <CircleHelp className="w-4 h-4" aria-hidden />
      </button>
      {open && (
        <Modal
          size="lg" title="Conversation statuses" description="What each status means and when to use it."
          onClick={() => setOpen(false)} closeButtonCLick={() => setOpen(false)}
          footer={<Button variant="outline" onClick={() => setOpen(false)}>Close</Button>}
        >
          <div className="space-y-5 max-h-[65vh] overflow-y-auto text-sm" data-testid="status-guide">
            <section aria-label="Whose turn">
              <h3 className="font-semibold text-foreground mb-1">Whose turn (automatic)</h3>
              <p className="text-xs text-muted-foreground mb-2">You never set these: they follow who wrote last. They change nothing about later RFPs.</p>
              <dl className="space-y-1.5">
                {TURN_GUIDE.map((t) => (
                  <div key={t.label}><dt className="inline font-medium text-foreground">{t.label}: </dt><dd className="inline text-muted-foreground">{t.meaning}</dd></div>
                ))}
              </dl>
            </section>
            <section aria-label="The result">
              <h3 className="font-semibold text-foreground mb-1">The result (your choice)</h3>
              <p className="text-xs text-muted-foreground mb-2">Every result can carry a short note. On the same RFP the newest result counts; across RFPs, Teaming wins, otherwise the newest of Interested / Not interested at all.</p>
              <div className="space-y-3">
                {RESULT_GUIDE.map((r) => {
                  const pill = r.key === "open" ? null : resultStatus(r.key);
                  return (
                    <div key={r.key} className="rounded-lg border border-border bg-surface-muted/50 p-3 space-y-1" data-testid={`guide-${r.key}`}>
                      <div className="flex items-center gap-2">
                        {pill ? <StatusPill tone={pill.tone} size="sm">{pill.label}</StatusPill> : <StatusPill tone="neutral" size="sm">Open</StatusPill>}
                      </div>
                      <p><span className="font-medium text-foreground">Use it when: </span><span className="text-muted-foreground">{r.when}</span></p>
                      <p><span className="font-medium text-foreground">This conversation: </span><span className="text-muted-foreground">{r.conversation}</span></p>
                      <p><span className="font-medium text-foreground">Later RFPs: </span><span className="text-muted-foreground">{r.future}</span></p>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </Modal>
      )}
    </>
  );
}
